import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import api from "@services/api";
import { orderKeys, usePack } from "@queries/order";
import { useAuth } from "@context/AuthContext";
import { paymentInfo } from "@lib/paymentMethods";
import "./css/order_summery.css";

function useDebouncedValue(value, delay = 250) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function dedupeCustomers(rows = []) {
  const seen = new Set();

  return rows.filter((row) => {
    const key = row?.customer_id ?? row?.id;

    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

export default function OrderSummary({ order }) {
  const { id: routeId } = useParams();
  const id = String(order?.order_id ?? routeId);
  const auth = useAuth();
  const queryClient = useQueryClient();
  const canUpdateDirectly = auth?.hasPermission?.(
    "sale/can_update_order_product_directly"
  );

  useEffect(() => {
    if (!id || !order) return;
    queryClient.setQueryData(orderKeys.one(id), (prev) => prev ?? order);
  }, [id, order, queryClient]);

  const { data: o } = useQuery({
    queryKey: orderKeys.one(id),
    queryFn: () => api.get(`/order/${id}`).then((r) => r.data),
    initialData: order,
    enabled: !!id,
  });
  const cur = o ?? order ?? {};

  const [editStore, setEditStore] = useState(false);
  const [pendingStoreId, setPendingStoreId] = useState(cur?.store_id ?? 0);

  const [editPayment, setEditPayment] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(cur?.payment_method ?? "cod");

  const [editCustomer, setEditCustomer] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSaveError, setCustomerSaveError] = useState("");

  const [editOrderType, setEditOrderType] = useState(false);
  const [pendingOrderType, setPendingOrderType] = useState(cur?.order_type ?? "normal");

  const [editInvoiceLabel, setEditInvoiceLabel] = useState(false);
  const [pendingInvoiceLabel, setPendingInvoiceLabel] = useState(cur?.invoice_label ?? "");

  const [isComplementary, setIsComplementary] = useState(!!cur?.complemantary_reason);
  const [complementaryReason, setComplementaryReason] = useState(cur?.complemantary_reason || "");

  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountText, setDiscountText] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");

  useEffect(() => {
    if (!editStore) setPendingStoreId(cur?.store_id ?? 0);
  }, [cur?.store_id, editStore]);

  useEffect(() => {
    if (!editPayment) setPendingPayment(cur?.payment_method ?? "cod");
  }, [cur?.payment_method, editPayment]);

  useEffect(() => {
    if (!editCustomer) {
      setCustomerSearch("");
      setSelectedCustomer(null);
      setCustomerSaveError("");
    }
  }, [editCustomer, cur?.customer_id]);

  useEffect(() => {
    if (!editOrderType) setPendingOrderType(cur?.order_type ?? "normal");
  }, [cur?.order_type, editOrderType]);

  useEffect(() => {
    if (!editInvoiceLabel) setPendingInvoiceLabel(cur?.invoice_label ?? "");
  }, [cur?.invoice_label, editInvoiceLabel]);

  useEffect(() => {
    setIsComplementary(!!cur?.complemantary_reason);
    setComplementaryReason(cur?.complemantary_reason || "");
  }, [cur?.complemantary_reason]);

  const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "--");
  const debouncedCustomerSearch = useDebouncedValue(customerSearch.trim(), 300);
  const customerSearchQuery = useQuery({
    queryKey: ["order", id, "customer-search", debouncedCustomerSearch],
    enabled:
      !!id &&
      editCustomer &&
      (debouncedCustomerSearch.length >= 2 || /^\d+$/.test(debouncedCustomerSearch)),
    queryFn: async () => {
      const term = debouncedCustomerSearch;
      const requests = [];

      if (/^\d+$/.test(term)) {
        requests.push(api.get("/customers", { params: { customer_id: term, page: 1, page_size: 10 } }));

        if (term.length >= 4) {
          requests.push(api.get("/customers", { params: { telephone: term, page: 1, page_size: 10 } }));
        }
      } else {
        requests.push(api.get("/customers", { params: { name: term, page: 1, page_size: 10 } }));

        if (term.includes("@")) {
          requests.push(api.get("/customers", { params: { email: term, page: 1, page_size: 10 } }));
        }
      }

      const responses = await Promise.all(requests);
      const rows = responses.flatMap((response) => response?.data?.data || []);

      return dedupeCustomers(rows);
    },
    staleTime: 10_000,
  });
  const customerSearchResults = customerSearchQuery.data || [];
  const currentCustomerId = cur.customer?.customer_id || cur.customer_id;
  // Invoice Label Name (WN-0113, per ORDER): shown in ( ) next to the name; empty = unchanged.
  const _invoiceLabel = (cur.invoice_label || "").trim();
  // Legacy nameless customers (registered before signup captured a name) leave
  // the order name blank — fall back to the phone so ops always sees who it is.
  const _rawName = (cur.customer?.name || cur.name || "").trim();
  const _phoneForName = cur.customer?.telephone || cur.telephone || "";
  const _displayName = _rawName || (_phoneForName ? `No name · ${_phoneForName}` : "No name");
  const currentCustomerName = _displayName + (_invoiceLabel ? ` (${_invoiceLabel})` : "");
  const currentCustomerEmail = cur.customer?.email || cur.email;
  const currentCustomerPhone = cur.customer?.telephone || cur.telephone;

  // Normalize a phone into a wa.me-ready international number, defaulting to
  // Jordan (+962). Numbers are stored various ways — bare local "0791234567",
  // 9-digit "791234567"/"777393236", "+962…", "00962…", "962…". wa.me needs
  // digits only, with a country code and no leading 0.
  const toWhatsappNumber = (raw) => {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    const hadPlus = s.startsWith("+");
    const d = s.replace(/\D/g, "");
    if (!d) return "";
    if (hadPlus) return d;                 // explicit international — trust it
    if (d.startsWith("00")) return d.slice(2); // 00<cc>… international prefix
    if (d.startsWith("962")) return d;         // already Jordan intl
    if (d.startsWith("0")) return "962" + d.slice(1); // local → Jordan
    return "962" + d;                      // bare number → default to Jordan
  };
  const currentCustomerWhatsapp = toWhatsappNumber(currentCustomerPhone);

  const PAYMENT_OPTIONS = [
    { value: "free_checkout", label: "free checkout" },
    { value: "cc", label: "credit card" },
    { value: "cod", label: "cash on delivery " },
    { value: "ccod", label: "credit card on delivery " },
    { value: "cliq", label: "CliQ" },
    { value: "bank_transfer", label: "Bank Transfer" },
  ];
  const ORDER_TYPE_OPTIONS = [
    "normal",
    "skip",
    "express",
    "disinfection",
    "furniture",
    "car_cleaning",
    "housekeeping",
    "carpets",
    "chemicals",
  ];

  const L = cur?.links || {};
  const withQ = (url, q) => (!url ? "" : url + (url.includes("?") ? "&" : "?") + q);

  const actions = useMemo(
    () => ({
      taxFree: withQ(L.simple_invoice, "no_tax=true"),
      businessInvoice: L.business_invoice,
      bagInvoice: L.bag_invoice,
      barcode: L.barcode,
      invoice: L.invoice,
      shipping: L.shipping,
      simpleInvoice: withQ(L.simple_invoice, "simple_invoice=true"),
      salim: L.create_salim_order_url,
      customer: L.customer_name,
      edit: L.edit ?? `/orders/${id}/edit`,
      cancel: L.cancel ?? "/orders",
    }),
    [L, id]
  );

  const invalidateRelated = () => {
    queryClient.invalidateQueries({ queryKey: orderKeys.one(id) });
    queryClient.invalidateQueries({ queryKey: orderKeys.products(id) });
    queryClient.invalidateQueries({ queryKey: orderKeys.totals(id) });
    queryClient.invalidateQueries({ queryKey: orderKeys.history(id) });
    queryClient.invalidateQueries({ queryKey: orderKeys.notes(id) });
    queryClient.invalidateQueries({ queryKey: ["orders"] });
  };

  const setOrderPatch = (patch) => {
    queryClient.setQueryData(orderKeys.one(id), (old) => ({ ...(old || cur || order), ...patch }));
  };

  const markPaid = useMutation({
    mutationFn: () => api.post(`/order/${id}/paid`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ unpaid: 0, paid_at: new Date().toISOString() });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const pack = usePack(id);

  const unPack = useMutation({
    mutationFn: () => api.post(`/order/${id}/unpack`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ packed: 0 });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const startPack = useMutation({
    mutationFn: () => api.post(`/order/${id}/start-pack`),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ start_pack: 1 });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const toggleOutsource = useMutation({
    mutationFn: () => {
      const current = queryClient.getQueryData(orderKeys.one(id)) || cur || order;
      const next = current?.outsource;
      return api.post(`/order/${id}/outsource`, { value: next });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ outsource: prev?.outsource ? 0 : 1 });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const toggleHasBag = useMutation({
    mutationFn: () => {
      const current = queryClient.getQueryData(orderKeys.one(id)) || cur || order;
      const next = current?.has_bag;
      return api.post(`/order/${id}/has-bag`, { value: next });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ has_bag: prev?.has_bag ? 0 : 1 });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  // The target value (0/1) is computed once at click time and passed in, so
  // mutationFn never re-reads the cache after onMutate has already flipped it
  // (onMutate runs BEFORE mutationFn) — that race made the toggle send the
  // inverse value and land the badge on the wrong label.
  const toggleTaxFree = useMutation({
    mutationFn: (nextVal) => api.post(`/order/${id}/tax-free`, { value: nextVal }),
    onMutate: async (nextVal) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ is_tax_free: nextVal });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const saveStore = useMutation({
    mutationFn: (store_id) => api.post(`/order/${id}/store`, { store_id }),
    onMutate: async (store_id) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ store_id });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: () => {
      setEditStore(false);
      invalidateRelated();
    },
  });

  const savePayment = useMutation({
    mutationFn: (payment_method) => api.post(`/order/${id}/payment`, { payment_method }),
    onMutate: async (payment_method) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ payment_method });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: () => {
      setEditPayment(false);
      invalidateRelated();
    },
  });

  const saveCustomer = useMutation({
    mutationFn: (customer_id) => api.post(`/order/${id}/customer`, { customer_id }).then((r) => r.data),
    onMutate: () => {
      setCustomerSaveError("");
    },
    onSuccess: (nextOrder) => {
      queryClient.setQueryData(orderKeys.one(id), nextOrder);
      setEditCustomer(false);
      invalidateRelated();
    },
    onError: (e) => {
      setCustomerSaveError(
        e?.response?.data?.message || e?.response?.data?.error || e?.message || "Failed to update customer."
      );
    },
  });

  const saveOrderType = useMutation({
    mutationFn: (order_type) => api.post(`/order/${id}/order-type`, { order_type }),
    onMutate: async (order_type) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ order_type });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: () => {
      setEditOrderType(false);
      invalidateRelated();
    },
  });

  const saveInvoiceLabel = useMutation({
    mutationFn: (invoice_label) => api.post(`/order/${id}/invoice-label`, { invoice_label }),
    onMutate: async (invoice_label) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ invoice_label });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: () => {
      setEditInvoiceLabel(false);
      invalidateRelated();
    },
  });

  const saveComplementary = useMutation({
    mutationFn: ({ isComplementary, reason }) =>
      api.post(`/order/${id}/complementary`, {
        is_complementary: !!isComplementary,
        reason: reason || null,
      }),
    onMutate: async ({ isComplementary, reason }) => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      setOrderPatch({ complemantary_reason: isComplementary ? reason || "" : null });
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: invalidateRelated,
  });

  const addDiscount = useMutation({
    mutationFn: ({ text, amount }) => api.post(`/order/${id}/discount`, { text, amount }),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: orderKeys.one(id) });
      const prev = queryClient.getQueryData(orderKeys.one(id));
      return { prev };
    },
    onError: (_e, _v, ctx) => ctx?.prev && queryClient.setQueryData(orderKeys.one(id), ctx.prev),
    onSettled: () => {
      setDiscountOpen(false);
      setDiscountText("");
      setDiscountAmount("");
      invalidateRelated();
    },
  });

  const isCcPayment = ["cc", "credit card", "creditcard", "payfort"].includes(
    (cur?.payment_method || "").trim().toLowerCase()
  );

  const StatRow = ({ label, tooltip, value, danger, success }) => (
    <tr>
      <td style={{ width: "1%" }}>
        <button className="btn btn-info btn-xs" title={tooltip} type="button">
          {label}
        </button>
      </td>
      <td className={danger ? "text-danger" : success ? "text-success" : ""}>
        {value ?? "--"}
      </td>
    </tr>
  );

  return (
    <div className="card">
      <div className="order-summary-row">
        <div className="tabs">
          <Link className="tab" to="/orders">
            Orders
          </Link>
          <span className="tab muted">Order #{cur?.order_id ?? id}</span>
        </div>

        <div className="order-actions">
          {actions.salim && (
            <a
              href={actions.salim}
              target="_blank"
              rel="noreferrer"
              title="Sallim and Istalim"
              className="btn btn-info"
              style={{ backgroundColor: "#72a192" }}
            >
              <i className="fa fa-exchange" />
            </a>
          )}
          {actions.businessInvoice && (
            <a
              href={actions.businessInvoice}
              target="_blank"
              rel="noreferrer"
              title="Business Invoice"
              className="btn btn-info"
            >
              <i className="fa fa-black-tie" />
            </a>
          )}
          {actions.taxFree && (
            <a
              href={actions.taxFree}
              target="_blank"
              rel="noreferrer"
              title="Receipt Tax free"
              className="btn btn-info"
            >
              <i className="fa fa-tint" style={{ fontStyle: "normal" }}>
                {" "}
                TF
              </i>
            </a>
          )}
          {actions.simpleInvoice && (
            <a
              href={actions.simpleInvoice}
              target="_blank"
              rel="noreferrer"
              title="Simple Receipt"
              className="btn btn-info"
            >
              <i className="fa fa-tint" />
            </a>
          )}
          {actions.bagInvoice && (
            <a
              href={actions.bagInvoice}
              target="_blank"
              rel="noreferrer"
              title="Bag Receipt"
              className="btn btn-info"
            >
              <i className="fa fa-shopping-bag" />
            </a>
          )}
          {actions.barcode && (
            <a
              href={actions.barcode}
              target="_blank"
              rel="noreferrer"
              title="Barcode"
              className="btn btn-info"
            >
              <i className="fa fa-barcode" />
            </a>
          )}
          {actions.invoice && (
            <a
              href={actions.invoice}
              target="_blank"
              rel="noreferrer"
              title="Print Invoice"
              className="btn btn-info"
            >
              <i className="fa fa-print" />
            </a>
          )}
          {actions.shipping && (
            <a
              href={actions.shipping}
              target="_blank"
              rel="noreferrer"
              title="Shipping Print"
              className="btn btn-info"
            >
              <i className="fa fa-truck" />
            </a>
          )}
          {actions.customer && (
            <a
              href={actions.customer}
              target="_blank"
              rel="noreferrer"
              title="Customer Name"
              className="btn btn-info"
            >
              <i className="fa fa-address-card" />
            </a>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h3 className="panel-title">
                <i className="fa fa-shopping-cart" />
                Order Details
              </h3>
            </div>
            <table className="table">
              <tbody>
                <tr>
                  <td style={{ width: "1%" }}>
                    <button className="btn btn-info btn-xs" title="Store" type="button">
                      <i className="fa fa-shopping-cart fa-fw" />
                    </button>
                  </td>
                  <td>
                    {!editStore ? (
                      <>
                        <a
                          href={cur.store_url || "#"}
                          target="_blank"
                          rel="noreferrer"
                          id="store_name_a"
                        >
                          {cur.store?.name || "WashyWash Facility"}
                        </a>
                        <span style={{ float: "right" }}>
                          <a role="button" onClick={() => setEditStore(true)}>
                            Change
                          </a>
                        </span>
                      </>
                    ) : (
                      <>
                        <select
                          value={pendingStoreId}
                          onChange={(e) => setPendingStoreId(Number(e.target.value))}
                        >
                          {(cur.stores || []).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                        <span style={{ float: "right" }}>
                          <button
                            type="button"
                            className="btn btn-info btn-xs"
                            onClick={() => saveStore.mutate(pendingStoreId)}
                          >
                            Save
                          </button>
                        </span>
                      </>
                    )}
                  </td>
                </tr>

                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Date Added" type="button">
                      <i className="fa fa-calendar fa-fw" />
                    </button>
                  </td>
                  <td>{fmtDate(cur.date_added)}</td>
                </tr>

                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Payment Method" type="button">
                      <i className="fa fa-credit-card fa-fw" />
                    </button>
                  </td>
                  <td>
                    {!editPayment ? (
                      <>
                        <span id="payment_method">
                          {paymentInfo(cur.payment_method).label}
                        </span>

                        <span style={{ float: "right" }}>
                          <a role="button" onClick={() => setEditPayment(true)}>
                            Change
                          </a>
                        </span>

                        {isCcPayment && <CardPaymentPanel orderId={id} />}
                      </>
                    ) : (
                      <>
                        <select
                          value={pendingPayment}
                          onChange={(e) => setPendingPayment(e.target.value)}
                        >
                          {PAYMENT_OPTIONS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                        <span style={{ float: "right" }}>
                          <button
                            type="button"
                            className="btn btn-info btn-xs"
                            onClick={() => savePayment.mutate(pendingPayment)}
                          >
                            Save
                          </button>
                        </span>
                      </>
                    )}
                  </td>
                </tr>

                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Shipping Method" type="button">
                      <i className="fa fa-truck fa-fw" />
                    </button>
                  </td>
                  <td>{cur.shipping_method || "Delivery fee"}</td>
                </tr>

                <tr id="order_type_tr">
                  <td>
                    <button className="btn btn-info btn-xs" title="Order Type" type="button">
                      <i className="fa fa-paper-plane fa-fw" />
                    </button>
                  </td>
                  <td>
                    {!editOrderType ? (
                      <>
                        <span id="order_type">{cur.order_type}</span>
                        <span style={{ float: "right" }}>
                          <a role="button" onClick={() => setEditOrderType(true)}>
                            Change
                          </a>
                        </span>
                      </>
                    ) : (
                      <>
                        <select
                          value={pendingOrderType}
                          onChange={(e) => setPendingOrderType(e.target.value)}
                        >
                          {ORDER_TYPE_OPTIONS.map((v) => (
                            <option key={v} value={v}>
                              {v}
                            </option>
                          ))}
                        </select>
                        <span style={{ float: "right" }}>
                          <button
                            type="button"
                            className="btn btn-info btn-xs"
                            onClick={() => saveOrderType.mutate(pendingOrderType)}
                          >
                            Save
                          </button>
                        </span>
                      </>
                    )}
                  </td>
                </tr>

                <tr id="invoice_label_tr">
                  <td>
                    <button className="btn btn-info btn-xs" title="Invoice Label Name" type="button">
                      <i className="fa fa-tag fa-fw" />
                    </button>
                  </td>
                  <td>
                    {!editInvoiceLabel ? (
                      <>
                        <span id="invoice_label">
                          {cur.invoice_label
                            ? `Invoice label: ${cur.invoice_label}`
                            : <span style={{ color: "#999" }}>Invoice label —</span>}
                        </span>
                        <span style={{ float: "right" }}>
                          <a role="button" onClick={() => setEditInvoiceLabel(true)}>
                            Change
                          </a>
                        </span>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={pendingInvoiceLabel}
                          placeholder="e.g. VIP — shown in ( ) next to the name"
                          onChange={(e) => setPendingInvoiceLabel(e.target.value)}
                          style={{ width: "58%" }}
                        />
                        <span style={{ float: "right" }}>
                          <button
                            type="button"
                            className="btn btn-info btn-xs"
                            onClick={() => saveInvoiceLabel.mutate(pendingInvoiceLabel)}
                          >
                            Save
                          </button>
                        </span>
                      </>
                    )}
                  </td>
                </tr>

                <tr>
                  <td>
                    <button
                      className="btn btn-danger btn-xs"
                      title="Complementary Order"
                      type="button"
                    >
                      <i className="fa fa-gift fa-fw" />
                    </button>
                  </td>
                  <td>
                    <label>
                      <input
                        type="checkbox"
                        checked={isComplementary}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setIsComplementary(v);
                          if (!v) {
                            setComplementaryReason("");
                            saveComplementary.mutate({ isComplementary: false, reason: null });
                          }
                        }}
                      />{" "}
                      Complementary Order
                    </label>
                  </td>
                </tr>

                {isComplementary && (
                  <tr id="complementaryRow">
                    <td>
                      <button
                        className="btn btn-danger btn-xs"
                        title="Complementary Order"
                        type="button"
                      >
                        <i className="fa fa-gift fa-fw" />
                      </button>
                    </td>
                    <td>
                      <select
                        value={complementaryReason}
                        onChange={(e) => setComplementaryReason(e.target.value)}
                        disabled={saveComplementary.isPending}
                      >
                        <option value="">Please select complementary reason before save</option>
                        {[
                          "Issue Type",
                          "Damage Issue",
                          "Delay Issue",
                          "Drying Issue",
                          "Dust & Stain Issue",
                          "Dust Issue",
                          "Packing Issue",
                          "Piece Fall in the Van",
                          "Sewing Issue",
                          "Shrinkage Issue",
                          "Smell Issue",
                          "Stain Issue",
                          "missing item",
                          "Fluff in the fabrics",
                          "deliver the order to wrong customer",
                          "Can’t clean it",
                          "Tailoring issue",
                          "Ironing",
                          "Color Issue",
                          "Tagging mistake",
                          "Deduction mistake",
                          "Financial issues",
                          "Test",
                          "Price Issue",
                          "didn’t deliver the whole order",
                          "didn’t add customer note",
                          "Wrong Customer",
                          "Marketing",
                          "Employee",
                          "Credit",
                        ].map((x) => (
                          <option key={x} value={x}>
                            {x}
                          </option>
                        ))}
                      </select>
                      <span style={{ float: "right" }}>
                        <button
                          type="button"
                          className={`btn ${saveComplementary.isSuccess ? 'btn-success' : 'btn-info'} btn-xs`}
                          disabled={saveComplementary.isPending}
                          onClick={() => {
                            if (!complementaryReason) {
                              alert("Please select a complementary reason before saving.");
                              return;
                            }
                            saveComplementary.mutate({
                              isComplementary,
                              reason: complementaryReason || null,
                            });
                          }}
                        >
                          {saveComplementary.isPending ? "Saving..." : saveComplementary.isSuccess ? "Saved ✓" : "Save"}
                        </button>
                      </span>
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="First Time Order" type="button">
                      <i className="fa fa-question fa-fw" />
                    </button>
                  </td>
                  <td>{cur.first_order ? "Yes" : "No"}</td>
                </tr>
                <tr>
                  <td>
                    <button
                      className="btn btn-info btn-xs"
                      title="Recycle Hanger"
                      type="button"
                    >
                      <i className="fa fa-recycle fa-fw" />
                    </button>
                  </td>
                  <td>{cur.recycle_hanger ? "Yes" : "No"}</td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="VIP" type="button">
                      <i className="fa fa-diamond" />
                    </button>
                  </td>
                  <td>No</td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Is Tax Free" type="button">
                      <i className="fa">TF</i>
                    </button>
                  </td>
                  <td>
                    {Number(cur.is_tax_free) ? "Yes" : "No"}
                    <span style={{ float: "right" }}>
                      <a role="button" onClick={() => toggleTaxFree.mutate(Number(cur.is_tax_free) ? 0 : 1)}>
                        Toggle
                      </a>
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Total Balance" type="button">
                      <i className="fa">$</i>
                    </button>
                  </td>
                  <td>
                    {(Number(cur.total) || 0).toFixed(2)}
                    {cur.currency_code || "JOD"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h3 className="panel-title">
                <i className="fa fa-user" />
                Customer Details
              </h3>
              {canUpdateDirectly && (
                <span style={{ float: "right" }}>
                  <button
                    type="button"
                    className="btn btn-link btn-xs"
                    style={{ padding: 0 }}
                    onClick={() => setEditCustomer((value) => !value)}
                  >
                    {editCustomer ? "Cancel" : "Change"}
                  </button>
                </span>
              )}
            </div>
            <table className="table">
              <tbody>
                {canUpdateDirectly && editCustomer && (
                  <>
                    <tr>
                      <td>
                        <button className="btn btn-info btn-xs" title="Search Customer" type="button">
                          <i className="fa fa-search fa-fw" />
                        </button>
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control input-sm"
                          value={customerSearch}
                          onChange={(e) => {
                            setCustomerSearch(e.target.value);
                            setSelectedCustomer(null);
                            setCustomerSaveError("");
                          }}
                          placeholder="Search by customer name, email, phone, or ID"
                        />
                        {customerSearchQuery.isFetching ? (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>Searching...</div>
                        ) : null}
                        {!customerSearchQuery.isFetching && customerSearch && customerSearchResults.length === 0 ? (
                          <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>No customers found.</div>
                        ) : null}
                        {customerSearchResults.length > 0 ? (
                          <div
                            style={{
                              marginTop: 8,
                              maxHeight: 180,
                              overflowY: "auto",
                              border: "1px solid #ddd",
                              borderRadius: 4,
                              backgroundColor: "#fff",
                            }}
                          >
                            {customerSearchResults.map((customerItem) => {
                              const customerItemId = customerItem.customer_id || customerItem.id;
                              const isSelected = selectedCustomer?.customer_id === customerItemId;

                              return (
                                <button
                                  key={customerItemId}
                                  type="button"
                                  className={`btn btn-link ${isSelected ? "text-primary" : "text-default"}`}
                                  style={{
                                    display: "block",
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "8px 12px",
                                    textDecoration: "none",
                                    backgroundColor: isSelected ? "#eef6ff" : "transparent",
                                    borderBottom: "1px solid #eee",
                                  }}
                                  onClick={() => {
                                    setSelectedCustomer(customerItem);
                                    setCustomerSearch(customerItem.name || String(customerItemId));
                                    setCustomerSaveError("");
                                  }}
                                >
                                  <strong>
                                    {customerItem.name} #{customerItemId}
                                  </strong>
                                  <div style={{ fontSize: 12, color: "#666" }}>
                                    {customerItem.telephone || "No phone"}
                                    {customerItem.email ? ` | ${customerItem.email}` : ""}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <button className="btn btn-info btn-xs" title="Selected Customer" type="button">
                          <i className="fa fa-check fa-fw" />
                        </button>
                      </td>
                      <td>
                        {selectedCustomer ? (
                          <>
                            <div>
                              <strong>{selectedCustomer.name}</strong> #{selectedCustomer.customer_id}
                            </div>
                            <div style={{ fontSize: 12, color: "#666" }}>
                              {selectedCustomer.telephone || "No phone"}
                              {selectedCustomer.email ? ` | ${selectedCustomer.email}` : ""}
                            </div>
                          </>
                        ) : (
                          <span style={{ color: "#666" }}>Select a customer from the search results.</span>
                        )}
                        <span style={{ float: "right" }}>
                          <button
                            type="button"
                            className="btn btn-info btn-xs"
                            disabled={!selectedCustomer || saveCustomer.isPending}
                            onClick={() => saveCustomer.mutate(selectedCustomer.customer_id)}
                          >
                            {saveCustomer.isPending ? "Saving..." : "Save"}
                          </button>
                        </span>
                        {customerSaveError ? (
                          <div style={{ marginTop: 8, fontSize: 12 }} className="text-danger">
                            {customerSaveError}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Customer ID" type="button">
                      <i className="fa fa-id-card fa-fw" />
                    </button>
                  </td>
                  <td>{currentCustomerId}</td>
                </tr>
                <tr>
                  <td style={{ width: "1%" }}>
                    <button className="btn btn-info btn-xs" title="Customer" type="button">
                      <i className="fa fa-user fa-fw" />
                    </button>
                  </td>
                  <td>
                  <Link to={`/customers/${currentCustomerId}`}>
                  {currentCustomerName}
                  </Link>
                  </td>
                  
                </tr>
                <tr>
                  <td>
                    <button
                      className="btn btn-info btn-xs"
                      title="Customer Group"
                      type="button"
                    >
                      <i className="fa fa-group fa-fw" />
                    </button>
                  </td>
                  <td>Default</td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="E-Mail" type="button">
                      <i className="fa fa-envelope-o fa-fw" />
                    </button>
                  </td>
                  <td>
                    <a href={`mailto:${currentCustomerEmail}`}>
                      {currentCustomerEmail}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href={`https://wa.me/${currentCustomerWhatsapp}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <button className="btn btn-info btn-xs" title="Telephone" type="button">
                        <i className="fa fa-phone fa-fw" />
                      </button>
                    </a>
                  </td>
                  <td>
                    <a
                      href={`https://wa.me/${currentCustomerWhatsapp}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {currentCustomerPhone}
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Platform" type="button">
                      <i className="fa fa-laptop fa-fw" />
                    </button>
                  </td>
                  <td>{cur.platform || "back-office"}</td>
                </tr>
                <tr>
                  <td>
                    <button className="btn btn-info btn-xs" title="Call Customer" type="button">
                      <i className="fa fa-mobile fa-fw" />
                    </button>
                  </td>
                  <td>no</td>
                </tr>

                <StatRow label="OFR" tooltip="Order frequency Rate" value={cur.stats?.offers ?? 0} />
                <StatRow
                  label="S"
                  tooltip="Customer active status"
                  value={cur.stats?.status_short ?? "Not Active"}
                  success={cur.stats?.status_short === "Active"}
                  danger={cur.stats?.status_short !== "Active"}
                />
                <StatRow label="Tier" tooltip="Customer Tier" value={cur.stats?.tier ?? "No orders last year"} />
                <StatRow label="AOV" tooltip="Average Order Value" value={cur.stats?.average ?? 0} />
                <StatRow label="LTV" tooltip="Total Order Value" value={cur.stats?.ltv ?? ""} />
              </tbody>
            </table>
          </div>
        </div>

        <div className="col-md-4">
          <div className="panel panel-default">
            <div className="panel-heading">
              <h3 className="panel-title">
                <i className="fa fa-cog" />
                Options
              </h3>
            </div>

            <table className="table">
              <tbody>
                <tr>
                  <td>Invoice</td>
                  <td id="invoice" className="text-right">
                    {cur.invoice_no ? `${cur.invoice_prefix || ''}${cur.invoice_no}` : 0}
                  </td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <a
                      href={actions.invoice}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success btn-xs"
                      title="Generate"
                    >
                      <i className="fa fa-cog" />
                    </a>
                  </td>
                </tr>

                {/* Disabled options per request */}
                <tr style={{ opacity: 0.5 }}>
                  <td>Reward Points</td>
                  <td className="text-right">0</td>
                  <td className="text-center">
                    <button
                      type="button"
                      disabled
                      className="btn btn-success btn-xs"
                      title="Disabled"
                      style={{ cursor: "not-allowed" }}
                    >
                      <i className="fa fa-plus-circle" />
                    </button>
                  </td>
                </tr>

                <tr style={{ opacity: 0.5 }}>
                  <td>Affiliate</td>
                  <td className="text-right">
                    0.00{cur.currency_code || "JOD"}
                  </td>
                  <td className="text-center">
                    <button
                      type="button"
                      disabled
                      className="btn btn-success btn-xs"
                      title="Disabled"
                      style={{ cursor: "not-allowed" }}
                    >
                      <i className="fa fa-plus-circle" />
                    </button>
                  </td>
                </tr>

                <tr style={{ opacity: 0.5 }}>
                  <td>Unpaid</td>
                  <td id="unpaid" className="text-right">
                    {cur.unpaid ? "" : "Paid"}
                  </td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      title="Disabled"
                      disabled
                      style={{ cursor: "not-allowed" }}
                    >
                      <i className="fa fa-money" />
                    </button>
                  </td>
                </tr>

                <tr>
                  <td style={{ color: cur.packed ? "green" : "coral" }}>
                    {cur.packed ? "UnPack Order" : "Pack Order"}
                  </td>
                  <td id="packOrder" className="text-right" style={{ color: cur.packed ? "green" : "coral" }}>
                    {cur.packed ? "Packed" : "Unpacked"}
                  </td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className={`btn btn-${cur.packed ? 'success' : 'danger'} btn-xs`}
                      title={cur.packed ? "UnPack" : "Pack"}
                      onClick={() => cur.packed ? unPack.mutate() : pack.mutate()}
                    >
                      <i className={cur.packed ? "fa fa-undo" : "fa fa-check-circle"} />
                    </button>
                  </td>
                </tr>

                <tr>
                  <td>start Pack Order</td>
                  <td id="startPackOrder" className="text-right">
                    {cur.start_pack ? "Started" : ""}
                  </td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      onClick={() => startPack.mutate()}
                    >
                      start packing
                    </button>
                  </td>
                </tr>

                <tr>
                  <td id="outsource_label">{cur.outsource ? "Outsourced" : " Not Outsource"}</td>
                  <td id="out_source" className="text-right"></td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      title="out source"
                      onClick={() => toggleOutsource.mutate()}
                    >
                      <i className="fa fa-check-circle" />
                    </button>
                  </td>
                </tr>

                <tr style={{ opacity: 0.5 }}>
                  <td>Add Reschedule Fee (#{cur.reschedule || 0})</td>
                  <td id="addRescheduleCharge" className="text-right"></td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      title="Disabled"
                      disabled
                      style={{ cursor: "not-allowed" }}
                    >
                      <i className="fa fa-plus-circle" />
                    </button>
                  </td>
                </tr>

                <tr style={{ opacity: 0.5 }}>
                  <td>Remove Reschedule Fee (#{cur.reschedule || 0})</td>
                  <td id="removeRescheduleCharge" className="text-right"></td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      title="Disabled"
                      disabled
                      style={{ cursor: "not-allowed" }}
                    >
                      <i className="fa fa-minus-circle" />
                    </button>
                  </td>
                </tr>

                <tr>
                  <td>Customer has a bag? ({cur.has_bag ? "Yes" : "No"})</td>
                  <td id="has_bag" className="text-right"></td>
                  <td className="text-center" style={{ width: "1%" }}>
                    <button
                      type="button"
                      className="btn btn-success btn-xs"
                      title="Change?"
                      onClick={() => toggleHasBag.mutate()}
                    >
                      <i className="fa fa-briefcase" />
                    </button>
                  </td>
                </tr>

                <tr>
                  <td>Created by: {cur.created_by || "—"}</td>
                  <td className="text-right"></td>
                  <td className="text-center" style={{ width: "1%" }}></td>
                </tr>

                <tr>
                  <td id="order_typeText" colSpan={3}>
                    <span id="order_type_text">
                      <span className="btn btn-success btn-xs"></span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Card-payment panel shown under "Change payment method" for cc / PayFort orders.
// Shows the REMAINING amount (red), lets staff charge a chosen amount (<= total)
// to the customer's saved card, and expands to each PayFort payment with a
// read-only "Re-verify" (CHECK_STATUS) button. Backend endpoints:
//   GET  /washy/order/{id}/payment-summary        -> totals + transactions
//   POST /washy/order/{id}/charge-amount {amount}  -> charge saved card
//   POST /washy/payment-transaction/{tx}/reverify  -> re-check with PayFort
function CardPaymentPanel({ orderId }) {
  const qc = useQueryClient();
  const [amt, setAmt] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [msg, setMsg] = useState(null); // {type:'ok'|'warn', text}

  const { data: sum, isLoading, refetch } = useQuery({
    queryKey: ["payment-summary", String(orderId)],
    queryFn: () => api.get(`/washy/order/${orderId}/payment-summary`).then((r) => r.data),
    staleTime: 15000,
  });

  // Default the editable amount to the remaining balance, once, when it loads.
  useEffect(() => {
    if (sum && amt === "" && sum.remaining != null) setAmt(Number(sum.remaining).toFixed(3));
  }, [sum]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = () => {
    refetch();
    qc.invalidateQueries({ queryKey: orderKeys.one(String(orderId)) });
    qc.invalidateQueries({ queryKey: orderKeys.totals(String(orderId)) });
    qc.invalidateQueries({ queryKey: orderKeys.history(String(orderId)) });
  };

  const charge = useMutation({
    mutationFn: (amount) =>
      api.post(`/washy/order/${orderId}/charge-amount`, { amount }).then((r) => r.data),
    onSuccess: (d) => {
      if (d?.status === "success") {
        setMsg({ type: "ok", text: `Charged ${Number(d.charged).toFixed(3)} JOD to the card.` });
      } else {
        setMsg({ type: "warn", text: d?.message || "Charge did not go through." });
      }
      refreshAll();
    },
    onError: (e) =>
      setMsg({ type: "warn", text: e?.response?.data?.message || e?.message || "Charge failed." }),
  });

  const reverify = useMutation({
    mutationFn: (txId) =>
      api.post(`/washy/payment-transaction/${txId}/reverify`).then((r) => r.data),
    onSuccess: () => refreshAll(),
  });

  if (isLoading || !sum) {
    return <div style={{ marginTop: 8, fontSize: 12, color: "#888" }}>Loading payment…</div>;
  }
  if (!sum.is_card) return null;

  const total = Number(sum.order_total || 0);
  const paid = Number(sum.paid || 0);
  const remaining = Number(sum.remaining || 0);
  const estimated = !!sum.estimated; // amount not confidently known from PayFort
  const hasCard = !!sum.has_saved_card;
  const txns = sum.transactions || [];
  const due = remaining > 0.0005;

  const doCharge = () => {
    const a = Number(amt);
    if (!(a > 0)) {
      setMsg({ type: "warn", text: "Enter an amount greater than 0." });
      return;
    }
    if (a > total + 0.0005) {
      setMsg({ type: "warn", text: `Amount can't exceed the order total (${total.toFixed(3)} JOD).` });
      return;
    }
    if (!window.confirm(`Charge ${a.toFixed(3)} JOD to the customer's saved card now?`)) return;
    setMsg(null);
    charge.mutate(a);
  };

  const red = "#c0392b";
  const green = "#188038";

  return (
    <div style={{ marginTop: 10, clear: "both" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <span style={{ color: due ? red : green, fontWeight: 700, fontSize: 14 }}>
          Remaining: {remaining.toFixed(3)} JOD
        </span>
        <span style={{ fontSize: 12, color: "#777" }}>
          (paid {paid.toFixed(3)} of {total.toFixed(3)})
        </span>
        {estimated && (
          <span style={{ fontSize: 11, color: "#b26a00" }} title="Captured amount could not be confirmed from PayFort">
            ⚠ estimated
          </span>
        )}
      </div>

      {hasCard ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
          <input
            type="number"
            step="0.01"
            min="0"
            max={total}
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            style={{ width: 110, padding: "5px 8px", border: "1px solid #ccc", borderRadius: 6 }}
          />
          <span style={{ fontSize: 12, color: "#777" }}>JOD · max {total.toFixed(2)}</span>
          <button
            type="button"
            onClick={doCharge}
            disabled={charge.isPending}
            style={{
              background: red, color: "#fff", border: "none", borderRadius: 6,
              padding: "6px 16px", fontWeight: 700, cursor: charge.isPending ? "default" : "pointer",
              opacity: charge.isPending ? 0.6 : 1,
            }}
          >
            {charge.isPending ? "Charging…" : "Pay"}
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>No saved card on file for this customer.</div>
      )}

      {msg && (
        <div style={{ marginTop: 6, fontSize: 12.5, color: msg.type === "ok" ? green : red }}>{msg.text}</div>
      )}

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{ marginTop: 8, background: "none", border: "none", color: "#2563eb", cursor: "pointer", fontSize: 12.5, padding: 0 }}
      >
        {expanded ? "▾ Hide PayFort payments" : `▸ Show PayFort payments (${txns.length})`}
      </button>

      {expanded && (
        <div style={{ overflowX: "auto", marginTop: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#666" }}>
                <th style={{ padding: "4px 8px" }}>Type</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Amount</th>
                <th style={{ padding: "4px 8px" }}>PayFort ID</th>
                <th style={{ padding: "4px 8px" }}>Date</th>
                <th style={{ padding: "4px 8px" }}></th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 && (
                <tr><td colSpan={5} style={{ padding: "6px 8px", color: "#999" }}>No PayFort payments recorded.</td></tr>
              )}
              {txns.map((t) => (
                <tr key={t.transaction_id} style={{ borderTop: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "5px 8px" }}>{t.type}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right" }}>
                    {t.amount != null ? `${Number(t.amount).toFixed(3)} JOD` : "—"}
                  </td>
                  <td style={{ padding: "5px 8px", fontFamily: "monospace", fontSize: 11.5 }}>{t.fort_id || "—"}</td>
                  <td style={{ padding: "5px 8px", whiteSpace: "nowrap", color: "#666" }}>{t.date || ""}</td>
                  <td style={{ padding: "5px 8px" }}>
                    <button
                      type="button"
                      onClick={() => reverify.mutate(t.transaction_id)}
                      disabled={reverify.isPending}
                      title="Re-check this payment's real amount with PayFort"
                      style={{ fontSize: 11, padding: "2px 8px", border: "1px solid #ccc", borderRadius: 5, background: "#fff", cursor: "pointer" }}
                    >
                      {reverify.isPending && reverify.variables === t.transaction_id ? "…" : "Re-verify"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
